import { useTranslation } from "react-i18next";
import HeaderView from "../map-asset/headerView";
import { Link, useNavigate } from "react-router-dom";
import { routeUrls } from "../../navigation/routeUrls";
import svgIcons from "../../assets/svg/svgIcons";
import HelpFaq from "./components/helpFaq";
import { useFetchAllArticlesApiQuery } from "../../api/network/helpApiServices";
import HelpMainSearch from "./components/helpMainSearch";
import { useEffect, useRef, useState } from "react";
import { HelpArticleReponse } from './type';
const PAGE_SIZE = 18;

const ScreenHelp = () => {
  const { t } = useTranslation("translation", { keyPrefix: "helpSection" });
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [allArticles, setAllArticles] = useState<HelpArticleReponse[]>([]);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasMoreRef = useRef(false);
  const isLoadingRef = useRef(false);

  const { data: allArticlesdata, isFetching: articlesLoading } =
    useFetchAllArticlesApiQuery({ page, page_size: PAGE_SIZE });

  //Binding isLoadingRef with articlesLoading value
  useEffect(() => {
    isLoadingRef.current = articlesLoading;
  }, [articlesLoading]);

  useEffect(() => {
    if (allArticlesdata?.results) {
      setAllArticles((prev) => {
        const newResults = allArticlesdata.results;
        if (prev.length === 0) return newResults;

        //For unique keys in allArticles
        const existingIds = new Set(prev.map((a) => a.id));
        const unique = newResults.filter((a) => !existingIds.has(a.id));
        if (unique.length === 0) {
          hasMoreRef.current = false;
          return prev;
        }
        return [...prev, ...unique];
      });
      hasMoreRef.current = !!allArticlesdata.next;
      isLoadingRef.current = false;
    }
  }, [allArticlesdata]);

  //Added a scroll event to get next page
  useEffect(() => {
    const handleScroll = () => {
      if (!sentinelRef.current || !hasMoreRef.current || isLoadingRef.current) return;
      if (sentinelRef.current.getBoundingClientRect().top <= window.innerHeight) {
        isLoadingRef.current = true;
        setPage((prev) => prev + 1);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <div className="flex items-center justify-between pr-5">
        <HeaderView
          title={t("heading")}
          showBackButton={true}
          backButtonCallback={() => navigate(routeUrls.dashboard)}
        />
      </div>
      <section className="text-center space-y-3 mb-8 mt-8">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 text-[60px]">
          {t("main_page_heading1")}
        </h1>
        <p className="text-gray-500 text-md">{t("main_page_heading2")}</p>
      </section>

      <HelpMainSearch />

      <section className="max-w-6xl mx-auto py-10">
        {allArticles.length === 0 && !articlesLoading ? (
          <div className="p-4 text-gray-500 flex items-center justify-center">
            {t("no_results_found")}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mx-4 lg:mx-0">
              {allArticles.map((tile) => (
                <Link key={tile.id} to={tile.slug}>
                  <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-md hover:shadow-lg transition duration-300 hover:scale-105 cursor-pointer h-full">
                    <div className="flex items-center gap-3 mb-3 p-1">
                      <img
                        src={tile.icon_url}
                        alt={tile.title}
                        className="w-8 h-8 object-contain bg-accent-blue-pale rounded-md p-1"
                      />
                      <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                        {tile.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500 leading-5">
                      {tile.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div ref={sentinelRef} className="h-4" />

            {articlesLoading && (
              <div className="relative z-10 w-full h-48">
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="loading-animation"
                  className="size-16 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                />
              </div>
            )}
          </>
        )}
      </section>

      <HelpFaq />
    </>
  );
};
export default ScreenHelp;